





































































































































import { FormBlock } from '@/app/components';
import { allowedProhibitedOptions, offlineRetentionOptions, requiredOptions } from '@/modules/asset/config/asset';
import { AssetTypeId } from '@/modules/asset/constants';
import { ContractStatus, ExpectedUsage, ExpectedUsageText } from '@/modules/sharing/constants';
import { PropType, computed, defineComponent } from '@vue/composition-api';
import HtmlDiff from 'htmldiff-js';
import * as R from 'ramda';
import { useContractMetadata } from '../../composable';
import { Contract, ContractTransaction, DerivationContract } from '../../types';
import LicenseDetail from './LicenseDetail.vue';

export default defineComponent({
    name: 'LicenseDetails',
    components: {
        FormBlock,
        LicenseDetail,
    },
    props: {
        contract: {
            type: Object as PropType<Contract | DerivationContract>,
            required: true,
        },
        onEditMode: {
            type: Boolean,
            default: false,
        },
        previousNegotiation: {
            type: Object as PropType<ContractTransaction>,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        barteringAsset: {
            type: Object as PropType<{ id: number; name: string; storageType?: string; structureType?: string }>,
            default: null,
        },
    },
    setup(props) {
        const contractRef = computed(() => props.contract);
        const barteringAssetRef = computed(() => props.barteringAsset);
        const { getLicensingSchema, getExpectedUpdatesSchema } = useContractMetadata(
            contractRef,
            props.disabled,
            barteringAssetRef,
        );
        const isDerivationContract = computed(() => 'sellerOrgId' in props.contract);
        const isModelAsset = computed(() =>
            isDerivationContract.value
                ? (props.contract as DerivationContract)?.sellingAsset?.assetTypeId === AssetTypeId.Model
                : props.contract?.asset?.assetTypeId === AssetTypeId.Model,
        );

        const getHtmlList = (texts: string[]) => {
            if (texts) {
                const result = ['<ul>'];
                texts.forEach((text: string) => {
                    result.push(`<li>${text}</li>`);
                });
                result.push('</ul>');
                return result.join('');
            }
            return null;
        };

        const derivation = computed(() => {
            if (!props.contract.metadata.license.derivation) return null;
            const options = Object.values(allowedProhibitedOptions);
            const newValues = [props.contract.metadata.license.derivation];
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.derivation
            ) {
                const oldValues = [props.previousNegotiation.changes.metadata.license.derivation];
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const attribution = computed(() => {
            if (!props.contract.metadata.license.attribution) return null;
            const options = Object.values(requiredOptions);
            const newValues = [props.contract.metadata.license.attribution];
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.attribution
            ) {
                const oldValues = [props.previousNegotiation.changes.metadata.license.attribution];
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const reproduction = computed(() => {
            if (!props.contract.metadata.license.reproduction) return null;
            const options = Object.values(allowedProhibitedOptions);
            const newValues = [props.contract.metadata.license.reproduction];
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.reproduction
            ) {
                const oldValues = [props.previousNegotiation.changes.metadata.license.reproduction];
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const distribution = computed(() => {
            if (!props.contract.metadata.license.distribution) return null;
            const options = Object.values(allowedProhibitedOptions);
            const newValues = [props.contract.metadata.license.distribution];
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.distribution
            ) {
                const oldValues = [props.previousNegotiation.changes.metadata.license.distribution];
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const expectedUsage = computed(() => {
            if (!props.contract.metadata.license.expectedUsage) return null;
            const options = Object.values(ExpectedUsageText);
            const newValues = props.contract.metadata.license.expectedUsage.map(
                (value: ExpectedUsage) => ExpectedUsageText[value],
            );
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.expectedUsage
            ) {
                const oldValues = props.previousNegotiation.changes.metadata.license.expectedUsage.map(
                    (value: ExpectedUsage) => ExpectedUsageText[value],
                );
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const offlineRetention = computed(() => {
            if (!props.contract.metadata.license.offlineRetention) return null;
            const options = R.pluck('value', R.clone(offlineRetentionOptions));
            const newValues = props.contract.metadata.license.offlineRetention;
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.offlineRetention
            ) {
                const oldValues = props.previousNegotiation.changes.metadata.license.offlineRetention;
                return { oldValues, newValues, options };
            }
            return { oldValues: undefined, newValues, options };
        });

        const expectedUpdates = computed(() => {
            if (!props.contract.metadata.license.expectedUpdates?.unit) return null;
            const currentExpectedUpdatesHtml = getHtmlList([
                `${props.contract.metadata.license.expectedUpdates?.value ?? ''} ${
                    props.contract.metadata.license.expectedUpdates?.unit
                }`,
            ]);
            if (
                props.contract.status === ContractStatus.Negotiate &&
                props.previousNegotiation?.changes.metadata?.license?.expectedUpdates
            ) {
                const previousExpectedUpdatesHtml = getHtmlList([
                    `${props.previousNegotiation.changes.metadata.license.expectedUpdates.value ?? ''} ${
                        props.previousNegotiation.changes.metadata.license.expectedUpdates.unit
                    }`,
                ]);
                return HtmlDiff.execute(previousExpectedUpdatesHtml, currentExpectedUpdatesHtml);
            }
            return currentExpectedUpdatesHtml;
        });

        return {
            isDerivationContract,
            isModelAsset,
            getLicensingSchema,
            getExpectedUpdatesSchema,
            ContractStatus,
            expectedUsage,
            offlineRetention,
            expectedUpdates,
            derivation,
            attribution,
            reproduction,
            distribution,
        };
    },
});
